/**
 * Fiscal Printer - low-level byte transport (send/receive over serial)
 */
import { ESC, CR } from './constants';
import { textToBytes, calculateChecksum } from './encoding';

/**
 * Read up to `timeout` ms of data from the serial port.
 * Returns Uint8Array or null if nothing received.
 *
 * @param {number} timeout - maksymalne okno oczekiwania (ms).
 * @param {object} [opts]
 * @param {number} [opts.idleGap] - jesli >0: gdy juz cos odebralismy i nastal cisza
 *   przez `idleGap` ms, konczymy wczesniej (nie czekamy do konca `timeout`).
 *   Uzywane dla <receipt action="close"> gdzie druk fizyczny trwa kilka sekund:
 *   dajemy dlugie okno (timeout), ale wracamy natychmiast po nadejsciu odpowiedzi.
 *   Dla zwyklych komend idleGap=0 -> zachowanie jak dawniej (szybko).
 */
export async function readResponse(port, timeout = 2000, opts = {}) {
  if (!port || !port.readable) return null;

  const idleGap = opts.idleGap || 0;
  const reader = port.readable.getReader();
  const chunks = [];
  const startTime = Date.now();
  let lastDataAt = 0;

  try {
    while (Date.now() - startTime < timeout) {
      const { value, done } = await Promise.race([
        reader.read(),
        new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 100))
      ]).catch(() => ({ value: null, done: false }));

      if (done) break;
      if (value) {
        chunks.push(value);
        lastDataAt = Date.now();
        await new Promise(r => setTimeout(r, 50));
      } else if (idleGap && lastDataAt && (Date.now() - lastDataAt) >= idleGap) {
        // Odebralismy odpowiedz i drukarka ucichla -> nie ma sensu czekac dalej.
        break;
      }
    }
  } catch (_) {
    // Timeout or end of data
  } finally {
    reader.releaseLock();
  }

  if (chunks.length === 0) return null;

  const totalLength = chunks.reduce((acc, chunk) => acc + chunk.length, 0);
  const result = new Uint8Array(totalLength);
  let offset = 0;
  for (const chunk of chunks) {
    result.set(chunk, offset);
    offset += chunk.length;
  }
  return result;
}

/**
 * Odczytaj i ODRZUC wszystko, co zalega w buforze portu (np. spozniona o ~2 s
 * odpowiedz <dle> z heartbeatu). Bez tego odczyt potwierdzenia paragonu moglby
 * zlapac stary <dle> zamiast wlasciwej odpowiedzi i uznac paragon za
 * niezafiskalizowany. Krotkie okno - oprozniamy tylko to, co juz przyszlo.
 */
export async function drainPort(port, ms = 250) {
  try {
    if (!port || !port.readable) return;
    await readResponse(port, ms);
  } catch (_) { /* ignore */ }
}

/**
 * Write raw bytes to the serial port.
 */
export async function sendRawBytes(port, bytes) {
  if (!port) throw new Error('Drukarka nie jest polaczona');
  const writer = port.writable.getWriter();
  try {
    await writer.write(new Uint8Array(bytes));
  } finally {
    writer.releaseLock();
  }
  return null;
}

/**
 * Send an XML string to the printer (Windows-1250 encoded).
 * Optionally waits for and parses the printer response.
 */
export async function sendXML(port, decoder, xmlString, checkResponse = true, opts = {}) {
  if (!port) throw new Error('Drukarka nie jest polaczona');
  if (!port.writable) throw new Error('Port drukarki nie jest zapisywalny (rozlaczono?)');

  // responseTimeout / idleGap pozwalaja wydluzyc okno odczytu dla <receipt close>
  // (druk fizyczny trwa), zostawiajac zwykle komendy szybkie (300ms).
  const responseTimeout = opts.responseTimeout || 300;
  const idleGap = opts.idleGap || 0;

  const bytes = textToBytes(xmlString);

  const writer = port.writable.getWriter();
  try {
    await writer.write(new Uint8Array(bytes));
  } finally {
    writer.releaseLock();
  }

  await new Promise(r => setTimeout(r, 150));

  if (checkResponse) {
    const response = await readResponse(port, responseTimeout, { idleGap });
    if (response) {
      const responseText = decoder.decode(response);
      // KRYTYCZNE: stary kod sprawdzal `responseText.includes('error=')` co dawalo
      // false positive na poprawnych odpowiedziach typu `papererror="no"`, `printererror="no"`,
      // `lastcommanderror="yes"`. Drukarka zwracala valid XML a sendXML rzucal bledem.
      // Teraz szukamy tylko PROPER tag <error code="..."> (rzeczywisty error packet).
      const errorMatch = responseText.match(/<error\s+code\s*=\s*"(\d+)"\s+description\s*=\s*"([^"]*)"/i);
      if (errorMatch) {
        const errorCode = errorMatch[1];
        const errorDesc = errorMatch[2];
        console.error(`Drukarka [XML]: BLAD: kod=${errorCode}, opis=${errorDesc}`);
        throw new Error(`Drukarka odrzucila komende: [${errorCode}] ${errorDesc}`);
      }

      return responseText;
    } else {
      // no response (timeout)
    }
  }

  return null;
}

/**
 * Send a Novitus ESC/POS command: ESC P [data] [checksum] ESC \
 */
export async function sendCommand(port, commandData, includeChecksum = true) {
  if (!port) throw new Error('Drukarka nie jest polaczona');

  const dataBytes = typeof commandData === 'string'
    ? textToBytes(commandData)
    : commandData;

  let fullCommand;
  if (includeChecksum) {
    const checksum = calculateChecksum(dataBytes);
    fullCommand = new Uint8Array([ESC, 0x50, ...dataBytes, ...checksum, ESC, 0x5C]);
  } else {
    fullCommand = new Uint8Array([ESC, 0x50, ...dataBytes, ESC, 0x5C]);
  }

  const writer = port.writable.getWriter();
  try {
    await writer.write(fullCommand);
  } finally {
    writer.releaseLock();
  }

  return null;
}

/**
 * Send ENQ (0x05) status byte and read the response.
 */
export async function sendENQ(port) {
  if (!port) throw new Error('Drukarka nie jest polaczona');
  const writer = port.writable.getWriter();
  try {
    await writer.write(new Uint8Array([0x05]));
  } finally {
    writer.releaseLock();
  }
  try {
    return await readResponse(port, 1000);
  } catch (_) {
    return null;
  }
}
